import { inject, Injectable, type Signal } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { ResourceTypes, RestApiClientService } from '@big-direkt/rest-api-client';
import { EnvironmentService } from '@big-direkt/utils/environment';
import { firstValueFrom } from 'rxjs';
import { UserRepository } from '../state/user.repository';
import { AuthenticationStrategy } from './authentication-strategy.interface';

@Injectable({
    providedIn: 'root',
})
export class ClassicAuthenticationStrategy implements AuthenticationStrategy {
    private readonly router = inject(Router);
    private readonly userRepository = inject(UserRepository);
    private readonly environmentService = inject(EnvironmentService);
    private readonly restApiClient = inject(RestApiClientService);

    public get isAuthenticated(): Signal<boolean> {
        return this.userRepository.isLoggedIn;
    }

    /* istanbul ignore next */
    public async setup(): Promise<void> {
        // Nothing to do here right now...
    }

    public login(destination?: string): UrlTree {
        const { privatePath } = this.environmentService;
        let loginUrl = `${privatePath}/login`;

        const cleanDestination = destination?.replace(/\/?logout/, '');

        if (cleanDestination) {
            loginUrl += `?destination=${cleanDestination}`;
        }

        return this.router.parseUrl(loginUrl);
    }

    public async logout(): Promise<void> {
        try {
            const token = this.getToken();
            await firstValueFrom(
                this.restApiClient.post(
                    ResourceTypes.UserLogout,
                    undefined,
                    undefined,
                    { headers: token ? { authorization: `Bearer ${token}` } : {} },
                ),
            );
        } finally {
            this.userRepository.setToken(undefined);

            if (this.router.url.startsWith(this.environmentService.privatePath)) {
                void this.router.navigateByUrl(this.login(this.router.url));
            }
        }
    }

    public getToken(): string | undefined {
        return this.userRepository.getToken();
    }
}
